import { useRef, useState, useEffect, useCallback } from 'react';

const tools = [
  {
    title: 'Expert Kredytowy',
    path: '/doradca360',
    desc: 'Kompleksowa analiza finansowania 360°',
    icon: 'ri-compass-3-line',
    color: 'bg-primary-500',
  },
  {
    title: 'Kalkulator nadpłaty',
    path: '/narzedzia/nadplata-kredytu',
    desc: 'Ile zaoszczędzisz nadpłacając',
    icon: 'ri-arrow-down-circle-line',
    color: 'bg-green-600',
  },
  {
    title: 'Kalkulator refinansowania',
    path: '/narzedzia/refinansowanie',
    desc: 'Oszczędność po przeniesieniu',
    icon: 'ri-exchange-dollar-line',
    color: 'bg-accent-500',
  },
  {
    title: 'Kalkulator zdolności',
    path: '/narzedzia/zdolnosc-kredytowa',
    desc: 'Ile kredytu możesz dostać',
    icon: 'ri-scales-3-line',
    color: 'bg-secondary-500',
  },
  {
    title: 'Leasing vs kredyt',
    path: '/narzedzia/leasing-vs-kredyt',
    desc: 'Leasing czy kredyt dla firmy?',
    icon: 'ri-arrow-left-right-line',
    color: 'bg-accent-500',
  },
  {
    title: 'Kalkulator konsolidacji',
    path: '/narzedzia/konsolidacja',
    desc: 'Połącz raty w jedną',
    icon: 'ri-stack-line',
    color: 'bg-primary-500',
  },
];

export default function ToolsSection() {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(1);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w >= 1280) setCardsPerView(4);
      else if (w >= 1024) setCardsPerView(3);
      else if (w >= 640) setCardsPerView(2);
      else setCardsPerView(1);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const maxSlide = Math.max(0, tools.length - cardsPerView);

  const handleScroll = useCallback(() => {
    if (!sliderRef.current) return;
    const el = sliderRef.current;
    const cardWidth = el.scrollWidth / tools.length;
    const idx = Math.round(el.scrollLeft / cardWidth);
    setCurrentSlide(Math.min(idx, maxSlide));
  }, [maxSlide]);

  const scrollToSlide = (idx: number) => {
    if (!sliderRef.current) return;
    const el = sliderRef.current;
    const cardWidth = el.scrollWidth / tools.length;
    el.scrollTo({
      left: idx * cardWidth,
      behavior: 'smooth',
    });
  };

  const prevSlide = () => {
    const next = Math.max(currentSlide - 1, 0);
    scrollToSlide(next);
  };

  const nextSlide = () => {
    const next = Math.min(currentSlide + 1, maxSlide);
    scrollToSlide(next);
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background-100">
      <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
        <div className="text-center mb-8 md:mb-12">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-primary-500 mb-3">
            Narzędzia finansowe
          </div>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-3">
            Sprawdź samodzielnie — zanim podejmiesz decyzję
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto leading-relaxed">
            Bezpłatne kalkulatory oparte na realnych algorytmach bankowych. Zero zapisów, zero danych osobowych — wszystko liczy się na Twoim urządzeniu.
          </p>
        </div>

        {/* Carousel — all breakpoints */}
        <div className="relative">
          <div className="flex items-center gap-2 md:gap-3">
            {/* left arrow */}
            <button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className="flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white border border-background-200 flex items-center justify-center text-foreground-600 hover:bg-background-50 disabled:opacity-30 disabled:cursor-default transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg md:text-xl" />
            </button>

            {/* slider track */}
            <div
              ref={sliderRef}
              onScroll={handleScroll}
              className="flex-1 overflow-x-auto scrollbar-hide snap-x snap-mandatory"
              style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
            >
              <div className="flex gap-3 md:gap-4">
                {tools.map((tool) => (
                  <a
                    key={tool.path}
                    href={tool.path}
                    className="flex-shrink-0 w-[260px] sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-12px)] xl:w-[calc(25%-12px)] snap-start text-left bg-white rounded-2xl border border-background-200 p-5 md:p-6 shadow-sm hover:shadow-md hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div
                      className={`w-11 h-11 rounded-xl ${tool.color} flex items-center justify-center mb-4`}
                    >
                      <i className={`${tool.icon} text-lg text-white`} />
                    </div>
                    <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 mb-1.5">
                      {tool.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed mb-4">
                      {tool.desc}
                    </p>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 transition-colors">
                      Otwórz
                      <i className="ri-arrow-right-line" />
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* right arrow */}
            <button
              onClick={nextSlide}
              disabled={currentSlide >= maxSlide}
              className="flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white border border-background-200 flex items-center justify-center text-foreground-600 hover:bg-background-50 disabled:opacity-30 disabled:cursor-default transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg md:text-xl" />
            </button>
          </div>

          {/* dots */}
          <div className="flex justify-center gap-1.5 mt-5">
            {Array.from({ length: maxSlide + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToSlide(i)}
                className={`h-2 rounded-full transition-all cursor-pointer ${
                  i === currentSlide
                    ? 'bg-primary-500 w-5'
                    : 'bg-background-300 hover:bg-background-400 w-2'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="text-center mt-8 md:mt-10">
          <a
            href="/narzedzia"
            className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-white font-semibold text-sm rounded-full hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Zobacz wszystkie narzędzia
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </section>
  );
}
